import * as React from "react";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import { useCpFormValue } from "../../gvc/testNewForm/utils";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { request } from "../../../services/cpln";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { CloudAccountGcpConfig } from "../../../schema/types/cloudaccount/entity";

export const GCPSummary: React.FC = () => {
  const navigate = useNavigate();
  const basePath = React.useContext(BasePathContext);
  const theme = useAppSelector((state) => state.theme.theme);

  const isEnabled = useCpFormValue("gcp.isEnabled");
  const cloudAccount = useCpFormValue("gcp.cloudAccount");
  const method = useCpFormValue("gcp.method");
  const serviceAccount = useCpFormValue("gcp.serviceAccount");
  const bindings = useCpFormValue("gcp.bindings");

  const gcpLogoPath = `/resources/logos/gcpLogo${theme === "dark" ? "Dark" : ""}.svg`;

  const [cloudAccountData, setCloudAccountData] = React.useState<CloudAccountGcpConfig | null>(null);
  const [isLoadingCA, setIsLoadingCA] = React.useState(false);

  React.useEffect(() => {
    if (isEnabled && cloudAccount.link) {
      fetchCloudAccount();
    } else {
      setCloudAccountData(null);
    }
  }, [isEnabled, cloudAccount.link]);

  async function fetchCloudAccount() {
    try {
      setIsLoadingCA(true);
      const res = await request({ url: cloudAccount.link });
      setCloudAccountData(res.data?.data as CloudAccountGcpConfig);
    } catch {
      setCloudAccountData(null);
    } finally {
      setIsLoadingCA(false);
    }
  }

  function handleClickLogo() {
    navigate(`${basePath}/cloud-access-gcp`);
  }

  if (!isEnabled) {
    return (
      <button
        onClick={handleClickLogo}
        style={{
          width: "100%",
          height: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--identity-gcp-logo-drop)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius)",
          cursor: "pointer",
          transition: "box-shadow var(--transition)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)")}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
      >
        <img src={gcpLogoPath} alt="GCP" style={{ height: 30 }} />
      </button>
    );
  }

  const caName = cloudAccount.value || parseLink(cloudAccount.link, { useInputCtx: true }).name;
  const projectId = cloudAccountData?.projectId;

  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: "var(--border-radius)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "4px solid var(--identity-gcp-logo-accent)",
        }}
      >
        <img src={gcpLogoPath} alt="GCP" style={{ height: 30 }} />
      </div>

      <div style={{ padding: "20px" }}>
        {isLoadingCA ? (
          <div className="flex justify-center py-4">
            <Spin />
          </div>
        ) : (
          <>
            <div className="flex mb-4">
              <div style={{ width: "33.33%" }}>
                <FormLabel>Cloud Account</FormLabel>
                {cloudAccount.link ? (
                  <a
                    href={`/console${cloudAccount.link}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/console${cloudAccount.link}`);
                    }}
                    className="color-link"
                  >
                    {caName}
                  </a>
                ) : (
                  <div>{caName}</div>
                )}
              </div>
              <div style={{ width: "66.67%" }}>
                <FormLabel>Project ID</FormLabel>
                <div style={{ fontFamily: "var(--font-mono, monospace)" }}>{projectId || "-"}</div>
              </div>
            </div>

            {method === "serviceAccount" && serviceAccount.value ? (
              <div className="mb-4">
                <FormLabel>Service Account</FormLabel>
                <div style={{ fontFamily: "var(--font-mono, monospace)" }}>{serviceAccount.value}</div>
              </div>
            ) : null}

            {method === "bindings" && bindings && bindings.length > 0 ? (
              <div className="mb-4">
                <FormLabel>Bindings</FormLabel>
                <div className="flex flex-col gap-2 mt-2">
                  {bindings.map((b: any) => (
                    <div
                      key={b.id}
                      style={{
                        border: "1px solid var(--color-border)",
                        borderTop: "3px solid var(--identity-gcp-logo-accent)",
                        borderRadius: "var(--border-radius)",
                        padding: "12px",
                      }}
                    >
                      <div className="mb-2">
                        <FormLabel>Resource</FormLabel>
                        <div style={{ fontFamily: "var(--font-mono, monospace)" }}>{b.resource}</div>
                      </div>
                      <div>
                        <FormLabel>Roles</FormLabel>
                        <ul style={{ margin: 0, paddingLeft: 20, listStyleType: "disc" }}>
                          {(b.roles || []).map((role: string, i: number) => (
                            <li key={i} style={{ fontFamily: "var(--font-mono, monospace)" }}>
                              {role}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        <div className="flex justify-end mt-4">
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate(`${basePath}/cloud-access-gcp`);
            }}
            className="color-link"
            style={{ cursor: "pointer", fontWeight: 500 }}
          >
            Details →
          </a>
        </div>
      </div>
    </div>
  );
};
